import { useState, useRef, useEffect, useCallback } from 'react';
import { cn } from '@/lib/utils';

interface TextCellProps {
  value: string | null;
  onChange: (value: string) => void;
  isEditing: boolean;
  onStartEdit: () => void;
  onEndEdit: () => void;
  placeholder?: string;
  maxLength?: number;
  className?: string;
}

export function TextCell({
  value,
  onChange,
  isEditing,
  onStartEdit,
  onEndEdit,
  placeholder = '',
  maxLength,
  className,
}: TextCellProps) {
  const [editValue, setEditValue] = useState(value ?? '');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing) {
      setEditValue(value ?? '');
      // Focus and select all text when entering edit mode
      setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 0);
    }
  }, [isEditing, value]);

  const handleSave = useCallback(() => {
    if (editValue !== value) {
      onChange(editValue);
    }
    onEndEdit();
  }, [editValue, value, onChange, onEndEdit]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSave();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setEditValue(value ?? '');
        onEndEdit();
      } else if (e.key === 'Tab') {
        handleSave();
        // Let the event bubble up for navigation
      }
    },
    [handleSave, onEndEdit, value]
  );

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        maxLength={maxLength}
        placeholder={placeholder}
        className={cn(
          'w-full h-full px-2 py-1 text-sm bg-white border-2 border-blue-500 outline-none',
          'rounded-none focus:ring-0',
          className
        )}
      />
    );
  }

  return (
    <div
      className={cn(
        'w-full h-full px-2 py-1 text-sm truncate cursor-text',
        !value && 'text-gray-400',
        className
      )}
      onDoubleClick={onStartEdit}
    >
      {value || placeholder}
    </div>
  );
}

export default TextCell;
